import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { CustomModal } from "@modules/core/components/base-modals";
import { FirebaseService, SocialMetaTagInfo } from "@core-services/firebase/firebase.service";
import { UserService } from "@main-services/user.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { ToastType } from "@modules/core/directives";
import { EMPTY, Observable } from "rxjs";
import {
	ASSET_PLACEHOLDER,
	FACEBOOK_SHARE_URL,
	LINKEDIN_SHARE_URL,
	REDDIT_SHARE_URL,
	REFERRER_PLACEHOLDER,
	SOCIAL_TRADING_ADP_SHARE_URL,
	SOCIAL_TRADING_SHARE_URL,
	TEXT_PLACEHOLDER,
	TWITTER_SHARE_URL,
	URL_PLACEHOLDER
} from "@modules/social-trading/components/share-preview/share-preview.constants";
import { ITradableChartData } from "@modules/main/components/tradable-chart/config/tradable-chart-data.interface";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { ShareType } from "@modules/social-trading/share-type.model";
import { config } from "@config";
import { Timespan } from "@helpers/const/timespan";

@Component({
	selector: "share-preview",
	templateUrl: "./share-preview.component.html",
	styleUrls: ["./share-preview.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class SharePreviewComponent extends CustomModal implements OnInit {
	@Input()
	public shareType: ShareType = ShareType.TRANSACTION;

	@Input()
	public showImage: boolean = true;

	@Input()
	public assetId: string = "";

	@Input()
	public sourceAssetId: string = "";

	@Input()
	public tradeType: TRADE_TYPE = null;

	@Input()
	public transactionDate: Date = new Date();

	public shareUrl$: Observable<string> = EMPTY;
	public toastType: ToastType = ToastType.success;
	public assetName: string = "";
	public tradableChartData: ITradableChartData = null;

	@Input()
	public locaKey: string = "";

	private readonly DEFAULT_SOCIAL_IMAGE_LINK: string = config.staticAssetLink + "social/trade-successful.jpg";

	public constructor(
		protected firebaseService: FirebaseService,
		protected userService: UserService,
		protected tradableListService: TradableListService
	) {
		super();
	}

	ngOnInit(): void {
		this.shareUrl$ = this.shareType === ShareType.ASSET_DETAIL ? this.getShareAdpUrl() : this.getShareTradeUrl();
		this.assetName = this.getTargetAssetName();
		if (this.showImage) {
			this.tradableChartData = this.getTradableChartData();
		}
	}

	public close(): void {
		this.modalService.popAll();
	}

	public back(): void {
		this.modalService.pop();
	}

	public getTitle(): string {
		const titleTranslationString = this.getTitleTranslationString();
		return this.translationService.translate(titleTranslationString, [
			this.sourceAssetId ? this.getSourceAssetName() : this.getTargetAssetName(),
			this.sourceAssetId ? this.getTargetAssetName() : ""
		]);
	}

	private getTitleTranslationString(): string {
		switch (this.tradeType) {
			case TRADE_TYPE.SELL:
				return "share_sell_message";
			case TRADE_TYPE.SWAP:
				return "share_swap_message";
			case TRADE_TYPE.BUY:
				return "share_purchase_message";
			default:
				throw new Error(`Trade type not supported for sharing: ${this.tradeType}`);
		}
	}

	public getTargetAssetName(): string {
		return this.tradableListService.getItemById(this.assetId).getName();
	}

	public getSourceAssetName(): string {
		if (!this.sourceAssetId) {
			return "";
		}
		return this.tradableListService.getItemById(this.sourceAssetId).getName();
	}

	public getShareTradeUrl(): Observable<string> {
		const refId = this.userService.user.getRefereeKey();
		const title = this.translationService.translate("share_title");
		const description = this.translationService.translate(this.locaKey, [this.getTargetAssetName(), " "]);
		const socialInfo: SocialMetaTagInfo = {
			socialTitle: title,
			socialDescription: description,
			socialImageLink: this.DEFAULT_SOCIAL_IMAGE_LINK
		};
		return this.firebaseService.shortenLink(this.generateShareTradeUrl(refId, this.assetId), socialInfo);
	}

	public getShareAdpUrl(): Observable<string> {
		const title = this.translationService.translate("share_title");
		const description = this.translationService.translate(this.locaKey, [this.getTargetAssetName(), " "]);
		const adpUrl = SOCIAL_TRADING_ADP_SHARE_URL.replace(ASSET_PLACEHOLDER, this.assetId);
		const socialInfo: SocialMetaTagInfo = {
			socialTitle: title,
			socialDescription: description,
			socialImageLink: this.DEFAULT_SOCIAL_IMAGE_LINK
		};
		return this.firebaseService.shortenLink(adpUrl, socialInfo);
	}

	public shareReddit(link: string, text: string): void {
		this.shareOnSocialPlatform(REDDIT_SHARE_URL, link, text);
	}

	public shareLinkedin(link: string): void {
		this.shareOnSocialPlatform(LINKEDIN_SHARE_URL, link);
	}

	public shareFacebook(link: string): void {
		this.shareOnSocialPlatform(FACEBOOK_SHARE_URL, link);
	}

	public shareTwitter(link: string, text: string): void {
		this.shareOnSocialPlatform(TWITTER_SHARE_URL, link, text);
	}

	private shareOnSocialPlatform(endpoint: string, link: string, text?: string): void {
		if (endpoint.includes(TEXT_PLACEHOLDER) && text === undefined) {
			throw new Error("Social platform requires text sharing which was not provided");
		}

		let url = endpoint.replace(URL_PLACEHOLDER, link);
		if (text) {
			url = url.replace(TEXT_PLACEHOLDER, text);
		}

		window.open(url, "_blank");
	}

	private generateShareTradeUrl(refId: string, assetId: string): string {
		return SOCIAL_TRADING_SHARE_URL.replace(REFERRER_PLACEHOLDER, refId).replace(ASSET_PLACEHOLDER, assetId);
	}

	public getTradableChartData(): ITradableChartData {
		const date = this.transactionDate;
		const transactionDate = this.toEpochString(date);
		const lastMonth = new Date(date.getTime());
		lastMonth.setMonth(date.getMonth() - 1);

		return {
			tradable: this.tradableListService.getItemById(this.assetId),
			timespan: Timespan.Year,
			startDate: this.toEpochString(lastMonth),
			endDate: transactionDate,
			title: this.getTitle(),
			transactionDate: this.transactionDate
		};
	}

	private toEpochString(date: Date): string {
		return Math.round(date.getTime() / 1000).toString();
	}
}
